import {
  BaseQueryApi,
  createApi,
  FetchArgs,
  fetchBaseQuery,
  FetchBaseQueryError,
} from "@reduxjs/toolkit/query/react";
import { Mutex } from "async-mutex";
import { RootState } from "../app/store";
import { logout, setCredentials, startLoggingOff, SESSION_REPLACED_MESSAGE, SESSION_REPLACED_STORAGE_KEY } from "../features/auth/authSlice";
import { setVehicles } from "../features/vehicles/vehiclesSlice";
import { LoginResponse } from "./authApi";

const mutex = new Mutex();
let sessionReplacementStarted = false;

const beginSessionReplacement = (api: BaseQueryApi) => {
  const state = api.getState() as RootState;
  if (
    sessionReplacementStarted ||
    state.auth.isLoggingOff ||
    !state.auth.isAuthenticated
  ) {
    return;
  }

  sessionReplacementStarted = true;
  sessionStorage.setItem(SESSION_REPLACED_STORAGE_KEY, SESSION_REPLACED_MESSAGE);
  api.dispatch(startLoggingOff());

  window.setTimeout(() => {
    api.dispatch(setVehicles([]));
    api.dispatch(logout());
    sessionReplacementStarted = false;
  }, 900);
};

export const baseUrl =
  process.env.REACT_APP_API_URL || "http://localhost:5071/api";
// const baseUrl = "https://adut-api.azurewebsites.net/api";

// Log the API URL for debugging
console.log("🔗 API Base URL:", baseUrl);

export const baseQuery = fetchBaseQuery({
  baseUrl,
  prepareHeaders: (headers, { getState }) => {
    const token = (getState() as RootState).auth.token;
    if (token) {
      headers.set("authorization", `Bearer ${token}`);
    }
    return headers;
  },
});

const baseQueryWithReauth = async (
  args: string | FetchArgs,
  api: BaseQueryApi,
  extraOptions: {},
) => {
  await mutex.waitForUnlock();

  // Debug logging
  const url = typeof args === "string" ? args : args.url;
  const fullUrl = `${baseUrl}/${url}`;
  console.log(
    "🌐 Making API request:",
    fullUrl,
    typeof args === "object" ? args : "",
  );

  let result = await baseQuery(args, api, extraOptions);

  console.log("📥 API Response:", result);

  // Handle 402 Payment Required (Subscription Required)
  if (result.error && (result.error as FetchBaseQueryError).status === 402) {
    const errorData = (result.error as any).data;
    console.warn("🚫 Subscription required:", errorData);

    // Store subscription error in session storage for display
    sessionStorage.setItem("subscriptionError", JSON.stringify(errorData));

    // Redirect based on user role
    if (errorData?.isAdmin) {
      window.location.href = "/subscription";
    } else {
      window.location.href = "/subscription-required";
    }

    return result;
  }

  if (result.error && (result.error as FetchBaseQueryError).status === 401) {
    const state = api.getState() as RootState;
    if (state.auth.isLoggingOff || !state.auth.isAuthenticated) {
      return result;
    }

    const isAuthSessionRequest =
      typeof url === "string" &&
      (url.includes("auth/login") ||
        url.includes("auth/logout") ||
        url.includes("auth/revoke-token") ||
        url.includes("auth/refresh-token"));

    if (isAuthSessionRequest) {
      return result;
    }

    if (!mutex.isLocked()) {
      const release = await mutex.acquire();

      try {
        const latestState = api.getState() as RootState;
        if (latestState.auth.isLoggingOff || !latestState.auth.isAuthenticated) {
          return result;
        }

        const refreshToken = latestState.auth.refreshToken;

        if (!refreshToken) {
          beginSessionReplacement(api);
          return result;
        }

        const refreshResult = await baseQuery(
          {
            url: "auth/refresh-token",
            method: "POST",
            body: { refreshToken },
          },
          api,
          extraOptions,
        );

        if (refreshResult.data) {
          // Extract the actual data from ApiResponse wrapper
          const authData =
            (refreshResult.data as any).data || refreshResult.data;
          api.dispatch(setCredentials(authData as LoginResponse));
          result = await baseQuery(args, api, extraOptions);
        } else {
          beginSessionReplacement(api);
        }
      } finally {
        release();
      }
    } else {
      await mutex.waitForUnlock();
      const latestState = api.getState() as RootState;
      if (latestState.auth.isLoggingOff || !latestState.auth.isAuthenticated) {
        return result;
      }
      result = await baseQuery(args, api, extraOptions);
    }
  }

  return result;
};

export const api = createApi({
  reducerPath: "api",
  baseQuery: baseQueryWithReauth,
  tagTypes: [
    "Vehicles",
    "Shops",
    "User",
    "UsersList",
    "Tenant",
    "TenantsList",
    "HosProfiles",
    "VehicleFaults",
    "Trailers",
    "TrailerLookups",
    "TruckAccounts",
    "TruckDrivers",
    "VehicleAssignments",
    "TrailerAssignments",
    "DispatcherAssignments",
    "Documents",
    "AbsenceRequests",
    "AbsenceBalances",
    "AbsencePolicies",
    "IntegrationAccount",
    "Subscription",
    "PaymentHistory",
    "AnyTrackLocations",
    "Claims",
    "DotInspections",
    "ShareLinks",
    "AuditLogs",
    "PtiHistory",
    "PtiCompliance",
    "Maintenance",
    "MaintenanceRecords",
    "TrailerMaintenanceRecords",
    "MaintenanceInvoices",
    "IftaSurcharges",
    "IftaProfile",
    "IftaGenerations",
    "Detentions",
    "Lumpers",
  ],
  endpoints: () => ({}),
});
